import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, PackageCheck, Rocket } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { CONSULTATION_URL } from "@/data/constants";

const TurnkeyBrands = () => {
  useSEO({
    title: "Turnkey Beverage Brands — Business-in-a-Box | FLOWTALIS™",
    description:
      "Skip the years of R&D. Acquire a fully built private-label beverage brand — domain, website, label design, and go-to-market kit included.",
    canonical: "https://flowtalis.com/turnkey",
  });

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/turnkey-brands")
      .then((r) => setBrands(r.data.brands || []))
      .catch(() => setBrands([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="ft-section-dark py-24" data-testid="turnkey-hero">
        <div className="ft-container max-w-4xl">
          <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-6">
            Business-in-a-Box
          </div>
          <span className="ft-gold-rule-lg" />
          <h1 className="mt-6 text-white text-[clamp(2.25rem,4.5vw,3.75rem)] font-bold leading-[1.02] tracking-tight mb-8">
            Fully built beverage brands,{" "}
            <span className="ft-serif text-[var(--ft-gold)]">ready to launch</span>.
          </h1>
          <p className="text-white/70 text-lg leading-relaxed max-w-2xl">
            Skip the years of R&amp;D, naming, trademarking, and design. FLOWTALIS™ Turnkey Brands
            are premium, fully developed private-label water brands — domain, website, label design,
            and go-to-market kit included. Acquire your brand today and launch this quarter.
          </p>
        </div>
      </section>

      {/* VALUE STRIP */}
      <section className="py-16 bg-white border-b border-[var(--ft-border)]" data-testid="turnkey-value-strip">
        <div className="ft-container grid md:grid-cols-4 gap-6">
          {[
            { Icon: Sparkles, t: "Fully Designed Brand", d: "Name, logo, label, and website already built." },
            { Icon: PackageCheck, t: "Turnkey Packages", d: "Choose Starter, Complete, or Launch & Inventory." },
            { Icon: ShieldCheck, t: "Manufactured by FLOWTALIS", d: "Same FDA-compliant partners that produce our client work." },
            { Icon: Rocket, t: "Launch in Weeks", d: "Acquire, transfer, and go to market — fast." },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="flex gap-4">
              <div
                className="h-11 w-11 rounded-full flex items-center justify-center shrink-0"
                style={{ background: "var(--ft-gold-soft)", color: "var(--ft-gold)" }}
              >
                <Icon size={20} strokeWidth={1.75} />
              </div>
              <div>
                <h3 className="font-semibold text-[var(--ft-black)]">{t}</h3>
                <p className="text-sm text-[var(--ft-muted)] mt-1 leading-relaxed">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BRAND GRID */}
      <section className="py-24 bg-[var(--ft-light-gray)]" data-testid="turnkey-grid-section">
        <div className="ft-container">
          <div className="max-w-2xl mb-14">
            <div className="ft-eyebrow mb-4">The Marketplace</div>
            <span className="ft-gold-rule-lg" />
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
              Available Turnkey Brands
            </h2>
            <p className="mt-4 text-[var(--ft-muted)] leading-relaxed">
              Each brand below is fully developed and available for acquisition through our consultative
              &quot;Inquire to Purchase&quot; process. Choose a brand, select a package, and our team will
              guide you through the handoff.
            </p>
          </div>

          {loading && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="turnkey-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="ft-card fx-skeleton h-96" />
              ))}
            </div>
          )}

          {!loading && brands.length === 0 && (
            <div className="fx-empty" data-testid="turnkey-empty">
              <div className="text-[var(--ft-muted)] py-8 text-center">
                New turnkey brands coming soon. Check back shortly.
              </div>
            </div>
          )}

          {!loading && brands.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="turnkey-grid">
              {brands.map((b) => {
                const isSold = b.status === "sold" || b.status === "archived";
                return (
                <Link
                  key={b.slug}
                  to={`/turnkey/${b.slug}`}
                  className={`ft-card fx-hover-lift p-0 overflow-hidden flex flex-col group ${isSold ? "opacity-90" : ""}`}
                  data-testid={`turnkey-card-${b.slug}`}
                >
                  <div
                    className="relative aspect-[4/5] bg-[var(--ft-black)] flex items-center justify-center overflow-hidden"
                    style={{
                      background:
                        "linear-gradient(135deg, #0d0d0d 0%, #191919 55%, #0a0a0a 100%)",
                    }}
                  >
                    {b.hero_image_url ? (
                      <img
                        src={b.hero_image_url}
                        alt={`${b.brand_name} — turnkey beverage brand`}
                        className={`w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-[1.03] ${isSold ? "grayscale-[40%]" : ""}`}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="text-[var(--ft-gold)] text-2xl font-bold tracking-[0.24em]">
                        {b.brand_name?.toUpperCase()}
                      </div>
                    )}
                    {isSold ? (
                      <div className="absolute top-4 left-4 text-[0.625rem] uppercase tracking-[0.28em] font-bold px-2.5 py-1 rounded-full"
                           style={{ background: "#7a1f1f", color: "#fff" }}
                           data-testid={`turnkey-card-sold-${b.slug}`}>
                        SOLD
                      </div>
                    ) : (
                      <div className="absolute top-4 left-4 text-[0.625rem] uppercase tracking-[0.28em] font-bold px-2.5 py-1 rounded-full"
                           style={{ background: "var(--ft-gold)", color: "var(--ft-black)" }}>
                        {b.status_label || "Available"}
                      </div>
                    )}
                    {b.design_edition_label && (
                      <div className="absolute top-4 right-4 text-[0.6rem] uppercase tracking-[0.22em] font-semibold px-2.5 py-1 rounded-full backdrop-blur"
                           style={{ background: "rgba(255,255,255,0.14)", color: "#fff" }}
                           data-testid={`turnkey-card-edition-${b.slug}`}>
                        {b.design_edition_label.split("·")[0].trim()}
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="text-[0.65rem] uppercase tracking-[0.24em] font-semibold text-[var(--ft-gold)] mb-1.5">
                      {isSold ? "Sold" : "Turnkey Brand"}
                    </div>
                    <h3 className="text-xl font-bold tracking-tight text-[var(--ft-black)]">
                      {b.brand_name}
                    </h3>
                    <div className="ft-gold-rule my-3" />
                    <p className="text-sm text-[var(--ft-muted)] leading-relaxed">{b.tagline}</p>
                    <div className="mt-6 pt-4 border-t border-[var(--ft-border)] flex items-center justify-between">
                      <span className="text-[0.75rem] uppercase tracking-[0.18em] font-semibold text-[var(--ft-charcoal)]">
                        {b.default_beverage_type || b.category}
                      </span>
                      <ArrowRight
                        size={18}
                        className="opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                        style={{ color: "var(--ft-gold)" }}
                      />
                    </div>
                  </div>
                </Link>
              );})}
            </div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-white" data-testid="turnkey-how">
        <div className="ft-container">
          <div className="max-w-2xl mb-14">
            <div className="ft-eyebrow mb-4">Acquisition Process</div>
            <span className="ft-gold-rule-lg" />
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
              A consultative handoff — <span className="ft-serif text-[var(--ft-gold)]">not</span> a
              checkout.
            </h2>
            <p className="mt-4 text-[var(--ft-muted)] leading-relaxed">
              Turnkey brands are premium acquisitions. Our team personally walks you through
              selection, financing, and launch — no automated checkout, no surprises.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { step: "01", t: "Browse & Select", d: "Explore available brands and choose a package that fits your ambition." },
              { step: "02", t: "Submit Inquiry", d: "Complete our short form. A FLOWTALIS specialist will contact you within one business day." },
              { step: "03", t: "Package & Terms", d: "We finalize your package selection, purchase terms, and hand-off timeline." },
              { step: "04", t: "Launch", d: "Domain, website, label, and manufacturing coordination — all transferred to you." },
            ].map((s) => (
              <div key={s.step} className="ft-card">
                <div className="text-[0.625rem] uppercase tracking-[0.32em] font-bold" style={{ color: "var(--ft-gold)" }}>
                  STEP {s.step}
                </div>
                <h3 className="mt-4 text-lg font-semibold text-[var(--ft-black)]">{s.t}</h3>
                <div className="ft-gold-rule my-3" />
                <p className="text-sm text-[var(--ft-muted)] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-24 ft-section-dark" data-testid="turnkey-cta">
        <div className="ft-container text-center max-w-2xl">
          <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-5">
            Not sure which brand fits?
          </div>
          <h2 className="text-white text-3xl md:text-4xl font-bold tracking-tight mb-6">
            Book a Turnkey Brand consultation.
          </h2>
          <p className="text-white/60 mb-8 leading-relaxed">
            30-minute conversation with a FLOWTALIS specialist to match you with the right brand
            and package for your market.
          </p>
          <a
            href={CONSULTATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="ft-btn-gold"
            data-testid="turnkey-book-consult"
          >
            Book a Consultation <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </PublicLayout>
  );
};

export default TurnkeyBrands;
